import ko from "knockout";
import koMapping from "knockout-mapping";
import metaCache from "flow/utils/metaCache";
import template from "./multiSelect.html";
import { MaybeObservable, MaybeObservableArray } from "flow/interfaces/MaybeObservable";

export interface IMultiSelectParams {
    items: MaybeObservableArray<any>;
    selectedValuePath: MaybeObservable<string>;
    selectedTextPath: MaybeObservable<string>;
    optionsCaption: MaybeObservable<string>;
    showSelectedLabels: MaybeObservable<boolean>;
    selectedItems: KnockoutObservableArray<any>;
    showSelectedCount: boolean;
    id: MaybeObservable<string>;
    placeholder: string;
    disabled: MaybeObservable<boolean>;
}

ko.components.register("ko-multiselect", {
    viewModel: function (params: IMultiSelectParams) {
        const items = ko.isObservable(params.items) ? params.items : ko.observableArray(params.items);
        const selectedValuePath = ko.isObservable(params.selectedValuePath)
            ? params.selectedValuePath
            : ko.observable(params.selectedValuePath);
        const selectedTextPath = ko.isObservable(params.selectedTextPath)
            ? params.selectedTextPath
            : ko.observable(params.selectedTextPath);
        const optionsCaption = ko.isObservable(params.optionsCaption)
            ? params.optionsCaption
            : ko.observable(params.optionsCaption);
        const showSelectedLabels =
            params.showSelectedLabels != null ? ko.observable(params.showSelectedLabels) : ko.observable(false);
        const selected = ko.isObservable(params.selectedItems)
            ? params.selectedItems
            : ko.observableArray(params.selectedItems);
        const showSelectedCount =
            params.showSelectedCount != null ? ko.observable(params.showSelectedCount) : ko.observable(false);
        const internalSelected = ko.observableArray<any>();
        const id = ko.isObservable(params.id) ? params.id : ko.observable(params.id);
        const searchTerm = ko.observable();
        const placeholder =
            params.placeholder != undefined ? params.placeholder : metaCache.translate("FlowHtml.Translations.Search");
        const disabled = ko.isObservable(params.disabled) ? params.disabled : ko.observable(false);
        const disabledStyles = ko.pureComputed(function () {
            return disabled() ? { "pointer-events": "none" } : { "pointer-events": "auto" };
        });

        const setInternalSelected = function () {
            for (let i = 0; i < items().length; i++) {
                for (let j = 0; j < selected().length; j++) {
                    if (selectedValuePath() && items()[i][selectedValuePath()]() === selected()[j]) {
                        internalSelected.push(koMapping.toJS(items()[i]));
                    }
                }
            }
        };
        setInternalSelected();

        if (selected) {
            if (selected.length > 0) {
                internalSelected(koMapping.toJS(params.selectedItems()));
            }
            selected.subscribe(function (newValue) {
                const tempItems: any[] = [];
                if (newValue) {
                    items().forEach(function (item) {
                        newValue.forEach(function (value) {
                            if (selectedValuePath()) {
                                if (item[selectedValuePath()]() === value) {
                                    tempItems.push(item);
                                }
                            } else {
                                if (item === value) {
                                    tempItems.push(item);
                                }
                            }
                        });
                    });
                    internalSelected(koMapping.toJS(tempItems));
                }
            });
        }

        const onRemove = function (item: any) {
            if (selectedValuePath()) {
                const option = item[selectedValuePath()];
                selected.remove(option);
            } else {
                selected.remove(item);
            }
        };

        const isSelected = function (item: any) {
            if (selectedValuePath()) {
                return selected.indexOf(item[selectedValuePath()]()) !== -1;
            } else {
                return selected.indexOf(item) !== -1;
            }
        };

        const onToggleSelection = function (item: any, event: Event) {
            if (selectedValuePath()) {
                const option = item[selectedValuePath()]();
                if (selected.indexOf(option) === -1) {
                    selected.push(option);
                } else {
                    selected.remove(option);
                }
            } else {
                if (selected.indexOf(item) === -1) {
                    selected.push(item);
                } else {
                    selected.remove(item);
                }
            }
            event.stopPropagation(); // This line keeps the dropdown open even after selecting the option.
        };

        const getSelectedText = ko.pureComputed({
            read: function () {
                const selectedText: string[] = [];
                internalSelected().forEach(function (selectedItem) {
                    if (selectedItem[selectedTextPath()]) {
                        selectedText.push(selectedItem[selectedTextPath()]);
                    }
                });
                return selectedText.join(", ");
            }
        });

        const getOptionsCaption = ko.pureComputed({
            read: function () {
                const displayCaptionText =
                    selected().length > 0
                        ? metaCache
                              .lookup("FlowHtml.Translations.Selected", "FlowHtml.Translations.Selected")
                              .toLowerCase()
                        : optionsCaption();
                if (showSelectedCount() && selected().length !== 0) {
                    return selected().length + " " + displayCaptionText;
                } else if (selected().length === 0) {
                    return displayCaptionText;
                }
                return undefined;
            }
        });

        const _isStringIncluded = function (item: any) {
            let str = "";
            let boolean = false;

            str = ko.isObservable(item[selectedTextPath()]) ? item[selectedTextPath()]() : item[selectedTextPath()];

            boolean = str ? str.toString().toLowerCase().includes(searchTerm().toLowerCase()) : false;

            return boolean;
        };

        const filteredItems = ko.pureComputed({
            read: function () {
                if (searchTerm()) {
                    return items().filter(_isStringIncluded);
                }
                return items();
            }
        });

        return {
            items: items,
            selectedValuePath: selectedValuePath,
            selectedTextPath: selectedTextPath,
            selected: selected,
            internalSelected: internalSelected,
            onRemove: onRemove,
            isSelected: isSelected,
            onToggleSelection: onToggleSelection,
            optionsCaption: optionsCaption,
            showSelectedLabels: showSelectedLabels,
            getSelectedText: getSelectedText,
            getOptionsCaption: getOptionsCaption,
            showSelectedCount: showSelectedCount,
            id: id,
            searchTerm: searchTerm,
            filteredItems: filteredItems,
            placeholder: placeholder,
            disabled: disabled,
            disabledStyles: disabledStyles
        };
    },
    template: template,
    synchronous: true
});
